import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CssBaseline, CircularProgress } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { EventProvider } from './contexts/EventContext';
import themeTokens from './theme';
import LoginPage from './pages/LoginPage';
import RsvpResponsePage from './pages/RsvpResponsePage';
import ProtectedRoute from './components/ProtectedRoute';

// Lazy load heavy components for better performance
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const StaffDashboard = lazy(() => import('./pages/StaffDashboard'));

// Loading fallback component
const LoadingFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
    <CircularProgress />
  </Box>
);

function ThemedApp() {
  const { user } = useAuth();
  const role = user?.role || 'admin';
  const palette = themeTokens.rolePalettes[role] || themeTokens.rolePalettes.admin;
  const muiTheme = createTheme({
    palette: {
      ...palette,
      background: palette.background,
    },
    shape: { borderRadius: themeTokens.radius },
    typography: themeTokens.typography,
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: themeTokens.radius,
            boxShadow: themeTokens.shadow[1],
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: themeTokens.radius,
            textTransform: 'none',
            fontWeight: 600,
          },
        },
      },
    },
  });
  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/rsvp" element={<RsvpResponsePage />} />
            <Route 
              path="/admin/*" 
              element={
                <ProtectedRoute role="admin">
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/staff/*" 
              element={
                <ProtectedRoute role="staff">
                  <StaffDashboard />
                </ProtectedRoute>
              } 
            />
            <Route path="/" element={<Navigate to="/login" replace />} />
          </Routes>
        </Suspense>
      </Box>
    </ThemeProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <EventProvider>
        <ThemedApp />
      </EventProvider>
    </AuthProvider>
  );
}

export default App; 